import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { CreditCard, Store, Building, Receipt, CheckCircle, ArrowRight, Shield, Gift, Percent } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

const PLANS = {
  exacer: { name: "Exacer-COLBACH", institution: "Colegio de Bachilleres", price: 999 },
  acredita: { name: "Acredita-Bach", institution: "CENEVAL", price: 999 },
};

const PAYMENT_METHODS = [
  { id: "card", name: "Tarjeta de credito/debito", icon: CreditCard, desc: "Visa, Mastercard, AMEX", time: "Inmediato" },
  { id: "oxxo", name: "OXXO", icon: Store, desc: "Paga en cualquier tienda OXXO", time: "1-3 dias" },
  { id: "spei", name: "Transferencia SPEI", icon: Building, desc: "Transferencia bancaria directa", time: "Inmediato a 2 hrs" },
  { id: "7eleven", name: "7-Eleven", icon: Receipt, desc: "Paga en cualquier 7-Eleven", time: "1-3 dias" },
];

export default function PaymentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get("program") || "");
  const [selectedMethod, setSelectedMethod] = useState("");
  const [processing, setProcessing] = useState(false);
  const [order, setOrder] = useState(null);
  const [discount, setDiscount] = useState(null);
  const [referralInput, setReferralInput] = useState(searchParams.get("ref") || "");
  const [applyingCode, setApplyingCode] = useState(false);

  useEffect(() => {
    // Check for existing discount
    fetch(`${API}/referrals/check-discount`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setDiscount(d); })
      .catch(() => {});
  }, []);

  const handlePayment = async () => {
    if (!selectedPlan) {
      toast.error("Selecciona un programa");
      return;
    }
    setProcessing(true);
    try {
      const res = await fetch(`${API}/payments/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: selectedPlan, payment_method: selectedMethod || "checkout" }),
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setOrder(data);
        if (data.checkout_url) {
          // Redirect to Conekta hosted checkout
          toast.success("Redirigiendo a la pasarela de pago...");
          window.location.href = data.checkout_url;
          return;
        }
        if (data.conekta_error) {
          toast.warning("La pasarela de pago no esta disponible temporalmente. Puedes simular el pago o intentar mas tarde.");
        } else if (data.status === "paid") {
          toast.success("Pago procesado exitosamente");
          await refreshUser();
        } else {
          toast.info("Orden creada. Completa tu pago.");
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Error procesando el pago");
      }
    } catch {
      toast.error("Error de conexion");
    }
    setProcessing(false);
  };

  const handleSimulatePayment = async () => {
    if (!order) return;
    setProcessing(true);
    try {
      const res = await fetch(`${API}/payments/simulate-payment/${order.order_id}`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        toast.success("Pago simulado exitosamente");
        await refreshUser();
        setTimeout(() => navigate("/dashboard"), 1500);
      }
    } catch {}
    setProcessing(false);
  };

  if (user?.subscription_status === "active") {
    return (
      <div className="min-h-screen bg-[#F8FAFC]" data-testid="payment-page">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="bg-white rounded-2xl p-12 border border-slate-100 shadow-sm">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#193B6E] mb-2" style={{ fontFamily: 'Outfit' }}>Ya eres miembro activo</h2>
            <p className="text-slate-500 mb-6">Tienes acceso completo a tu programa</p>
            <button
              onClick={() => navigate("/dashboard")}
              className="bg-[#20D3C0] text-[#112C54] font-semibold rounded-full px-8 py-3 hover:bg-[#1AB8A6] transition-all"
              data-testid="go-dashboard-btn"
            >
              Ir al panel
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (order && order.status !== "paid") {
    return (
      <div className="min-h-screen bg-[#F8FAFC]" data-testid="payment-page">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-12">
          <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-[0_8px_30px_rgba(25,59,110,0.08)]">
            <h2 className="text-2xl font-bold text-[#193B6E] mb-6" style={{ fontFamily: 'Outfit' }}>Tu orden</h2>

            <div className="bg-[#F8FAFC] rounded-xl p-6 border border-slate-100 mb-6">
              <p className="text-sm text-slate-500 mb-2">Plan: <span className="font-semibold text-[#193B6E]">{order.plan_name || PLANS[order.plan]?.name}</span></p>
              <p className="text-sm text-slate-500 mb-2">Monto: <span className="font-semibold text-[#193B6E]">${order.amount_display} MXN</span></p>
              {order.discount_percentage > 0 && (
                <p className="text-sm text-green-600">Descuento aplicado: {order.discount_percentage}%</p>
              )}
              <p className="text-sm text-slate-500">Estatus: <span className={`font-semibold ${order.status === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>{order.status}</span></p>
            </div>

            {order.checkout_url && (
              <a
                href={order.checkout_url}
                className="w-full block text-center bg-[#20D3C0] text-[#112C54] hover:bg-[#1AB8A6] font-semibold rounded-xl px-6 py-3 transition-all mb-4"
                data-testid="go-to-checkout-btn"
              >
                Ir a pagar con Conekta
              </a>
            )}

            {order.conekta_error && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <p className="text-sm text-amber-700">
                  <strong>Nota:</strong> {order.conekta_error}
                </p>
              </div>
            )}

            <button
              onClick={async () => {
                const res = await fetch(`${API}/payments/orders/${order.order_id}/status`, { credentials: "include" });
                if (res.ok) {
                  const data = await res.json();
                  if (data.status === "paid") {
                    toast.success("Pago confirmado!");
                    await refreshUser();
                    navigate("/dashboard", { replace: true });
                  } else {
                    toast.info(`Estatus actual: ${data.status}`);
                    setOrder(prev => ({ ...prev, status: data.status }));
                  }
                }
              }}
              className="w-full bg-[#193B6E] text-white hover:bg-[#132D55] font-semibold rounded-xl px-6 py-3 transition-all mb-4"
              data-testid="check-status-btn"
            >
              Verificar estatus del pago
            </button>

            {/* Fallback simulate payment for sandbox */}
            <button
              onClick={handleSimulatePayment}
              disabled={processing}
              className="w-full border-2 border-slate-200 text-slate-500 hover:border-[#20D3C0] hover:text-[#193B6E] font-medium rounded-xl px-6 py-3 transition-all disabled:opacity-50 text-sm"
              data-testid="simulate-payment-btn"
            >
              {processing ? "Procesando..." : "Simular pago (modo prueba)"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="payment-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-[#193B6E] mb-2" style={{ fontFamily: 'Outfit' }}>Hazte miembro</h1>
        <p className="text-slate-500 mb-8">Accede a todo el contenido premium sin publicidad</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Plan Selection */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <h3 className="text-lg font-semibold text-[#193B6E] mb-4" style={{ fontFamily: 'Outfit' }}>1. Elige tu programa</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.entries(PLANS).map(([id, plan]) => (
                  <button
                    key={id}
                    onClick={() => setSelectedPlan(id)}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      selectedPlan === id ? 'border-[#20D3C0] bg-[#20D3C0]/5' : 'border-slate-200 hover:border-[#20D3C0]/50'
                    }`}
                    data-testid={`plan-${id}`}
                  >
                    <h4 className="font-semibold text-[#193B6E]">{plan.name}</h4>
                    <p className="text-xs text-slate-500 mt-1">{plan.institution}</p>
                    <p className="text-2xl font-bold text-[#193B6E] mt-2" style={{ fontFamily: 'Outfit' }}>${plan.price} <span className="text-sm font-normal text-slate-400">MXN</span></p>
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Method Info */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <h3 className="text-lg font-semibold text-[#193B6E] mb-4" style={{ fontFamily: 'Outfit' }}>2. Metodos de pago disponibles</h3>
              <p className="text-sm text-slate-500 mb-4">Al proceder, seras redirigido a la pasarela segura de Conekta donde podras elegir tu metodo de pago:</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {PAYMENT_METHODS.map((method) => (
                  <div key={method.id} className="text-center p-3 bg-[#F8FAFC] rounded-xl border border-slate-100">
                    <method.icon className="w-5 h-5 text-[#20D3C0] mx-auto mb-2" />
                    <p className="text-xs font-semibold text-[#193B6E]">{method.name}</p>
                    <p className="text-xs text-slate-400 mt-1">{method.time}</p>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={handlePayment}
              disabled={!selectedPlan || processing}
              className="w-full bg-[#20D3C0] text-[#112C54] hover:bg-[#1AB8A6] font-semibold rounded-xl px-6 py-4 transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-lg shadow-md"
              data-testid="process-payment-btn"
            >
              {processing ? (
                <div className="animate-spin w-5 h-5 border-2 border-[#112C54] border-t-transparent rounded-full"></div>
              ) : (
                <>Proceder al pago con Conekta <ArrowRight className="w-5 h-5" /></>
              )}
            </button>
          </div>

          {/* Summary */}
          <div>
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm sticky top-24">
              <h3 className="text-lg font-semibold text-[#193B6E] mb-4" style={{ fontFamily: 'Outfit' }}>Resumen</h3>
              {selectedPlan ? (
                <>
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{PLANS[selectedPlan]?.name}</span>
                      <span className="font-semibold text-[#193B6E]">${PLANS[selectedPlan]?.price}</span>
                    </div>
                    {discount && discount.discount_percentage > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-500 flex items-center gap-1"><Percent className="w-3 h-3" /> Descuento referido</span>
                        <span className="font-semibold text-green-500">-${Math.round(PLANS[selectedPlan]?.price * discount.discount_percentage / 100)}</span>
                      </div>
                    )}
                    <div className="h-px bg-slate-100"></div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-[#193B6E]">Total</span>
                      <span className="text-xl font-bold text-[#193B6E]" style={{ fontFamily: 'Outfit' }}>
                        ${discount && discount.discount_percentage > 0
                          ? Math.round(PLANS[selectedPlan]?.price * (1 - discount.discount_percentage / 100))
                          : PLANS[selectedPlan]?.price
                        } MXN
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2 text-xs text-slate-400">
                    <p className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> Videos ilimitados sin publicidad</p>
                    <p className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> Material PDF completo</p>
                    <p className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> Cuestionarios de practica</p>
                    <p className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> Clases en vivo por Google Meet</p>
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-400">Selecciona un programa</p>
              )}
              <div className="mt-6 flex items-center gap-2 text-xs text-slate-400">
                <Shield className="w-4 h-4" /> Pago seguro con Conekta
              </div>
            </div>

            {/* Referral code input */}
            {!user?.referred_by && (
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm mt-4" data-testid="payment-referral-section">
                <div className="flex items-center gap-2 mb-3">
                  <Gift className="w-5 h-5 text-[#20D3C0]" />
                  <h4 className="font-semibold text-[#193B6E] text-sm">Codigo de referido</h4>
                </div>
                <p className="text-xs text-slate-500 mb-3">Ingresa un codigo de referido para obtener 10% de descuento</p>
                <div className="flex gap-2">
                  <Input
                    value={referralInput}
                    onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
                    placeholder="Ej: MARIA1234"
                    className="font-mono uppercase text-sm"
                    data-testid="payment-referral-input"
                  />
                  <button
                    onClick={async () => {
                      if (!referralInput.trim()) return;
                      setApplyingCode(true);
                      try {
                        const res = await fetch(`${API}/referrals/apply`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ referral_code: referralInput.trim() }),
                          credentials: "include",
                        });
                        const data = await res.json();
                        if (res.ok) {
                          toast.success(data.message);
                          const discRes = await fetch(`${API}/referrals/check-discount`, { credentials: "include" });
                          if (discRes.ok) setDiscount(await discRes.json());
                        } else {
                          toast.error(data.detail || "Codigo no valido");
                        }
                      } catch { toast.error("Error"); }
                      setApplyingCode(false);
                    }}
                    disabled={applyingCode || !referralInput.trim()}
                    className="bg-[#20D3C0] text-[#112C54] font-semibold rounded-lg px-4 py-2 text-sm hover:bg-[#1AB8A6] transition-all disabled:opacity-50 whitespace-nowrap"
                    data-testid="payment-apply-referral-btn"
                  >
                    {applyingCode ? "..." : "Aplicar"}
                  </button>
                </div>
              </div>
            )}
            {user?.referred_by && discount && discount.discount_percentage > 0 && (
              <div className="bg-green-50 rounded-xl p-4 border border-green-200 mt-4" data-testid="discount-applied">
                <p className="text-sm text-green-700 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" /> Descuento de {discount.discount_percentage}% aplicado
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
